import { element } from "./dom.js";
import type { PartState } from "./part-state.js";
import { record, strings } from "./sheet-data.js";
import {
  type FlowPart,
  SheetFlow,
  sheetCue,
  sheetHanzi,
  sheetNumber,
  sheetRows,
} from "./sheet-flow.js";
import type { GlyphLibrary } from "./worksheet-drawing.js";
import type { SheetLabels } from "./worksheet-page.js";

/**
 * The character structure sheet 汉字结构, as pages ready to print.
 *
 * Three parts and the answers. **Structure** gives a character with its
 * reading and meaning, and four pictures of structures to circle one of.
 * **Radical** asks for the radical as the character writes it, in a square.
 * **Parts** asks for the components the character divides into, a square
 * each. The answers name each one.
 *
 * What each character is asked with is worked out at build time, in
 * `src/content/worksheets/structure-data.ts`, and fetched as one file.
 */

/** The parts a structure sheet prints. */
export type StructurePart = "structure" | "radical" | "parts" | "key";

/** A structure as the data file holds it: short name, pinyin, English, picture. */
export type StructureLabel = readonly [
  short: string,
  pinyin: string,
  english: string,
  icon: string,
];

/** One character, as the data file holds it. */
export interface StructureCharacter {
  readonly reading: string;
  readonly cue: string;
  readonly structure: string | undefined;
  readonly radical: string | undefined;
  readonly radicalCue: string | undefined;
  readonly parts: readonly string[] | undefined;
}

/** The data file. */
export interface StructureFile {
  /** The structures in the order `/jiegou/` lists them, by name. */
  readonly structures: ReadonlyMap<string, StructureLabel>;
  readonly characters: ReadonlyMap<string, StructureCharacter>;
}

function optional(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

/** The data file read defensively, or nothing for a body that is not one. */
export function structureFile(body: unknown): StructureFile | undefined {
  const file = record(body);
  const structures = record(file?.["structures"]);
  const characters = record(file?.["characters"]);
  if (structures === undefined || characters === undefined) {
    return undefined;
  }
  const labels = new Map<string, StructureLabel>();
  for (const [name, value] of Object.entries(structures)) {
    const label = strings(value);
    if (label?.length === 4) {
      labels.set(name, [label[0]!, label[1]!, label[2]!, label[3]!]);
    }
  }
  const read = new Map<string, StructureCharacter>();
  for (const [character, value] of Object.entries(characters)) {
    const entry = record(value);
    if (
      typeof entry?.["reading"] !== "string" ||
      typeof entry["cue"] !== "string"
    ) {
      continue;
    }
    const structure = optional(entry["structure"]);
    read.set(character, {
      reading: entry["reading"],
      cue: entry["cue"],
      structure:
        structure !== undefined && labels.has(structure)
          ? structure
          : undefined,
      radical: optional(entry["radical"]),
      radicalCue: optional(entry["radicalCue"]),
      parts: strings(entry["parts"]),
    });
  }

  return { structures: labels, characters: read };
}

/** One character on the sheet. */
export interface StructureItem {
  readonly character: string;
  readonly entry: StructureCharacter;
}

/** The characters the sheet asks about, and those it has nothing on. */
export interface StructureChoice {
  readonly items: readonly StructureItem[];
  readonly missing: readonly string[];
}

/** Which of the three exercises a sheet asks. */
export type StructureAsked = Readonly<
  Record<Exclude<StructurePart, "key">, boolean>
>;

/** Whether an exercise the sheet asks has something to ask of a character. */
function asksOf(entry: StructureCharacter, asked: StructureAsked): boolean {
  return (
    (asked.structure && entry.structure !== undefined) ||
    (asked.radical && entry.radical !== undefined) ||
    (asked.parts && entry.parts !== undefined)
  );
}

/**
 * The characters the sheet's exercises have something to ask of, in the order
 * they were given.
 *
 * A character none of them can ask about is missing, so a sheet of radicals
 * alone leaves out 女, which is its own radical, and says so.
 * @param asked - The exercises the sheet prints.
 * @param limit - The most characters a sheet takes.
 */
export function structureItems(
  file: StructureFile,
  characters: readonly string[],
  asked: StructureAsked,
  limit: number,
): StructureChoice {
  const items: StructureItem[] = [];
  const missing: string[] = [];
  for (const character of characters) {
    const entry = file.characters.get(character);
    if (entry === undefined || !asksOf(entry, asked)) {
      missing.push(character);
    } else if (items.length < limit) {
      items.push({ character, entry });
    }
  }

  return { items, missing };
}

/**
 * The structures a learner is offered, the commonest first, from which the
 * three wrong pictures on an item are taken.
 */
export const offeredStructures: readonly string[] = [
  "左右结构",
  "上下结构",
  "独体字",
  "全包围结构",
  "左下包围结构",
  "左上包围结构",
];

/**
 * The four structures an item offers, worked out from its number so that a
 * sheet prints the same every time and the answer moves about.
 * @param known - The structures the file names, so a picture is never missing.
 */
export function structureOptions(
  number: number,
  answer: string,
  known: ReadonlySet<string>,
): readonly string[] {
  const others = offeredStructures.filter(
    (name) => name !== answer && known.has(name),
  );
  const wrong = Array.from(
    { length: Math.min(3, others.length) },
    (_, at) => others[(number + at) % others.length]!,
  );
  const options = [...wrong];
  options.splice(number % (wrong.length + 1), 0, answer);

  return options;
}

/** Each part's rows, in millimetres. */
export const structureHeights = {
  structure: 21,
  radical: 27,
  parts: 21,
  key: 6,
} as const;

/**
 * The least each part's rows can be drawn at, on a page drawn closer to take
 * the last of its part. Measured in the browser, where a structure row and a
 * parts row hold 18.8 mm, a radical row 16.9 mm and an answer 5 mm.
 */
export const structureLeast = {
  structure: 19.5,
  radical: 19,
  parts: 19.5,
  key: 5.5,
} as const;

/** The square a radical or a part is written in. */
const square = 12;

/** A picture from the data file, as a node, or an empty one if it is not SVG. */
function picture(svg: string): Element {
  const parsed = new DOMParser().parseFromString(svg, "image/svg+xml");
  const root = parsed.documentElement;

  return root.localName === "svg"
    ? document.importNode(root, true)
    : element("span");
}

/** One line of the answer key: the character and its answer, marked. */
function answer(
  number: number,
  character: string,
  mark: string,
  english = "",
): HTMLElement {
  return element("div", {
    className: "cb-wf-key-item",
    children: [
      sheetNumber(number),
      element("span", {
        className: "cb-wf-hz",
        attributes: { lang: "zh-Hans" },
        children: [
          document.createTextNode(`${character} `),
          element("span", { className: "cb-wf-mark", text: mark }),
        ],
      }),
      element("span", { className: "cb-wf-en", text: english }),
    ],
  });
}

/** What drawing the sheet needs. */
export interface StructureSheetProperties {
  readonly library: GlyphLibrary;
}

/** One question, numbered. */
interface Question {
  readonly number: number;
  readonly item: StructureItem;
}

/** Draws structure sheets. */
export class StructureSheet {
  readonly #library: GlyphLibrary;

  constructor(properties: StructureSheetProperties) {
    this.#library = properties.library;
  }

  /** Every page of the sheet, leaving out the parts not asked for. */
  pages(
    state: PartState<StructurePart>,
    items: readonly StructureItem[],
    structures: ReadonlyMap<string, StructureLabel>,
    labels: SheetLabels,
  ): readonly HTMLElement[] {
    let number = 0;
    const numbered = (
      asked: boolean,
      has: (item: StructureItem) => boolean,
    ): readonly Question[] =>
      asked
        ? items.filter(has).map((item) => ({ number: (number += 1), item }))
        : [];
    const structure = numbered(
      state.structure,
      (item) => item.entry.structure !== undefined,
    );
    const radical = numbered(
      state.radical,
      (item) => item.entry.radical !== undefined,
    );
    const parts = numbered(
      state.parts,
      (item) => item.entry.parts !== undefined,
    );

    const known = new Set(structures.keys());
    const flow: FlowPart[] = [];
    let part = 0;
    if (structure.length > 0) {
      flow.push({
        number: (part += 1),
        title: "Structure",
        chinese: "结构",
        note: "Circle the picture that matches each character's structure.",
        rows: sheetRows(
          structure.map((question) =>
            this.#structureItem(question, structures, known),
          ),
          2,
          structureHeights.structure,
          "cb-wj-structures",
          structureLeast.structure,
        ),
      });
    }
    if (radical.length > 0) {
      flow.push({
        number: (part += 1),
        title: "Radical",
        chinese: "部首",
        note: "Write each character's radical in the square. Use the form shown in the character.",
        rows: sheetRows(
          radical.map((question) => this.#radicalItem(question)),
          4,
          structureHeights.radical,
          "cb-wj-radicals",
          structureLeast.radical,
        ),
      });
    }
    if (parts.length > 0) {
      flow.push({
        number: (part += 1),
        title: "Parts",
        chinese: "拆字",
        note: "Split each character once into two or three parts. Write one part in each square.",
        rows: sheetRows(
          parts.map((question) => this.#partsItem(question)),
          2,
          structureHeights.parts,
          "cb-wj-split",
          structureLeast.parts,
        ),
      });
    }
    if (state.key && flow.length > 0) {
      flow.push(...this.#key(structure, radical, parts, structures));
    }

    return new SheetFlow({
      paper: state.paper,
      kind: "Character structure",
      kindChinese: "汉字结构",
      parts: flow,
      labels,
    }).pages();
  }

  /** The character with its reading and meaning, as every item opens. */
  #character(item: StructureItem): HTMLElement {
    return element("div", {
      className: "cb-wj-character",
      children: [
        sheetHanzi(item.character),
        sheetCue(item.entry.reading, item.entry.cue),
      ],
    });
  }

  #structureItem(
    { number, item }: Question,
    structures: ReadonlyMap<string, StructureLabel>,
    known: ReadonlySet<string>,
  ): HTMLElement {
    const options = structureOptions(number, item.entry.structure!, known).map(
      (name) => {
        const label = structures.get(name)!;

        return element("span", {
          className: "cb-wj-option",
          children: [picture(label[3]), sheetHanzi(label[0])],
        });
      },
    );

    return element("div", {
      className: "cb-wj-item",
      children: [
        sheetNumber(number),
        this.#character(item),
        element("div", { className: "cb-wj-options", children: options }),
      ],
    });
  }

  #radicalItem({ number, item }: Question): HTMLElement {
    return element("div", {
      className: "cb-wj-radical",
      children: [
        sheetNumber(number),
        this.#character(item),
        this.#library.row(square, [{ kind: "empty" }], undefined, "tracing"),
      ],
    });
  }

  #partsItem({ number, item }: Question): HTMLElement {
    const squares = item.entry.parts!.flatMap((_, at) => [
      ...(at === 0 ? [] : [element("span", { text: "+" })]),
      this.#library.row(square, [{ kind: "empty" }], undefined, "tracing"),
    ]);

    return element("div", {
      className: "cb-wj-item",
      children: [
        sheetNumber(number),
        this.#character(item),
        element("div", {
          className: "cb-wj-sum",
          children: [element("span", { text: "=" }), ...squares],
        }),
      ],
    });
  }

  #key(
    structure: readonly Question[],
    radical: readonly Question[],
    parts: readonly Question[],
    structures: ReadonlyMap<string, StructureLabel>,
  ): readonly FlowPart[] {
    const keyed: FlowPart[] = [];
    const add = (title: string, items: readonly HTMLElement[]): void => {
      if (items.length > 0) {
        keyed.push({
          number: keyed.length + 1,
          title,
          rows: sheetRows(
            items,
            3,
            structureHeights.key,
            "",
            structureLeast.key,
          ),
          startsPage: keyed.length === 0,
        });
      }
    };
    add(
      "Structure",
      structure.map(({ number, item }) => {
        const label = structures.get(item.entry.structure!)!;

        return answer(number, item.character, label[0], label[2]);
      }),
    );
    add(
      "Radical",
      radical.map(({ number, item }) =>
        answer(
          number,
          item.character,
          item.entry.radical!,
          item.entry.radicalCue,
        ),
      ),
    );
    add(
      "Parts",
      parts.map(({ number, item }) =>
        answer(number, item.character, item.entry.parts!.join(" + ")),
      ),
    );

    return keyed;
  }
}
